import { BaseError, decodeErrorResult, parseAbi, RawContractError, type Hex } from 'viem';

/**
 * 0x Settler / AllowanceHolder custom errors (from `0xProject/0x-settler`
 * `src/core/SettlerErrors.sol`, `IERC20` encoded as `address`). `Error(string)`
 * and `Panic(uint256)` are intentionally absent -- viem's `decodeErrorResult`
 * appends them itself.
 */
const KNOWN_ERRORS_ABI = parseAbi([
  'error TooMuchSlippage(address token, uint256 expected, uint256 actual)',
  'error ZeroBuyAmount(address buyToken)',
  'error ZeroSellAmount(address token)',
  'error BoughtSellToken(address sellToken)',
  'error DeltaNotPositive(address token)',
  'error DeltaNotNegative(address token)',
  'error SignatureExpired(uint256 deadline)',
  'error PermitExpired()',
  'error PermitFailed()',
  'error InvalidSignature()',
  'error InvalidSignatureLen()',
  'error UnsupportedPermitType()',
  'error MsgValueMismatch(uint256 expected, uint256 actual)',
  'error Underpayment(uint256 msgValueMin, uint256 msgValueActual)',
  'error ConfusedDeputy()',
  'error InvalidTarget()',
  'error InvalidSender()',
  'error ForwarderNotAllowed()',
  'error ActionInvalid(uint256 i, bytes4 action, bytes data)',
  'error UnknownForkId(uint8 forkId)',
  'error UnknownPoolManagerId(uint8 poolManagerId)',
  'error InvalidOffset()',
  'error NotConverged()',
  'error TokenHashCollision(address token0, address token1)',
  'error ZeroToken()',
  'error InvalidFeeToken()',
  'error InvalidTokenAmountsLength()',
]);

/**
 * Pulls raw revert bytes from a thrown viem error, mirroring viem's own
 * `getContractError` walk: find the node carrying `data`, then unwrap the
 * `{ data }`-vs-string node shapes RPCs return.
 */
export function extractRevertData(err: unknown): Hex | undefined {
  if (!(err instanceof BaseError)) return undefined;
  const node = err.walk(
    (e) => e instanceof RawContractError || (e != null && typeof e === 'object' && 'data' in e),
  ) as { data?: unknown } | null;
  const raw = node?.data;
  const hex =
    raw != null && typeof raw === 'object' && 'data' in raw ? (raw as { data: unknown }).data : raw;
  return typeof hex === 'string' && hex.startsWith('0x') ? (hex as Hex) : undefined;
}

export function decodeRevertData(data: Hex): string {
  try {
    const { errorName, args } = decodeErrorResult({ abi: KNOWN_ERRORS_ABI, data });
    // viem appends Error/Panic at runtime but not to the static ABI type; widen to compare.
    const name: string = errorName;
    if (name === 'Error') return String(args?.[0] ?? 'Error');
    if (name === 'Panic') return `Panic(${args?.[0] ?? ''})`;
    return name;
  } catch {
    // Unknown selector: keep the raw 4 bytes.
    return data.slice(0, 10);
  }
}
